import { useCallback, useState, type FormEvent } from 'react';

import { fullReleaseWaitlist } from '~/marketing/landingContent';
import { captureEvent, presenceEvents } from '../presence/posthogPresence';
import { consumeWaitlistSignupSource } from './waitlistSignupSource';
import { WAITLIST_METROS, WAITLIST_METRO_OTHER } from './waitlistMetros';
import {
  fullReleaseWaitlistEnabled,
  getWaitlistDoor,
  submitFullReleaseWaitlist,
} from './fullReleaseWaitlist';

/**
 * The share link deliberately carries NO id.
 *
 * It used to be `/?ref=<row id>`, which had two problems. Nothing read it — launch notification is
 * `ORDER BY id ASC` over unnotified rows, with no referral, position or priority column anywhere —
 * so "help them skip the line" was a reward with no mechanism. And the id is the row's sequential
 * primary key, so anyone handed a link could read off exactly how many people had signed up, which
 * is the one number a pre-launch waitlist should not publish.
 *
 * Keeping the current path means a link shared from /for-her still lands on /for-her.
 */
function buildReferralUrl(): string {
  if (typeof window === 'undefined') return 'https://kynward.com';
  return `${window.location.origin}${window.location.pathname}`;
}

export function FullReleaseWaitlistForm() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [citySlug, setCitySlug] = useState('');
  // Where they typed when no listed metro fits. Rides along in submission metadata so an unlisted
  // city is still countable when deciding which metro opens next.
  const [cityOther, setCityOther] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [referralId, setReferralId] = useState<number | undefined>();
  const [referralCopied, setReferralCopied] = useState(false);

  const enabled = fullReleaseWaitlistEnabled();
  const referralUrl = referralId != null ? buildReferralUrl() : undefined;

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!enabled || status === 'loading') return;
      setStatus('loading');
      setMessage('');
      setReferralId(undefined);
      setReferralCopied(false);
      // Read once and share across all three events. consumeWaitlistSignupSource() RESETS on read,
      // so calling it per event would attribute the attempt and leave the outcome anonymous.
      // 'none' rather than omitting the property: an absent field and the default landing look
      // identical in a funnel breakdown, and comparing the three doors is the point.
      const door = getWaitlistDoor() ?? 'none';
      const source = consumeWaitlistSignupSource();
      // citySlug rides on every event. It used to reach the API and never the analytics tool, so
      // city and door lived in different systems and no funnel could answer "how many Austin
      // signups, by door" — which is the only question a city-by-city rollout needs to ask.
      captureEvent(presenceEvents.waitlist_full_release_attempt, { door, source, city: citySlug });
      const result = await submitFullReleaseWaitlist({
        email,
        firstName,
        citySlug,
        ...(citySlug === WAITLIST_METRO_OTHER && cityOther.trim()
          ? { cityOther: cityOther.trim() }
          : {}),
      });
      if (!result.ok) {
        captureEvent(presenceEvents.waitlist_full_release_error, { door, source, city: citySlug });
        setStatus('error');
        setMessage(result.message);
        return;
      }
      const { data } = result;
      captureEvent(presenceEvents.waitlist_full_release_submit, {
        created: data.created,
        already_registered: data.alreadyRegistered,
        door,
        source,
        city: citySlug,
      });
      setStatus('success');
      setMessage(
        data.alreadyRegistered ? fullReleaseWaitlist.successAgain : fullReleaseWaitlist.successNew
      );
      if (data.id != null) {
        setReferralId(data.id);
      }
    },
    [email, firstName, citySlug, cityOther, enabled, status]
  );

  const onCopyReferralLink = useCallback(async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      setReferralCopied(false);
    }
  }, [referralUrl]);

  if (!enabled) {
    return (
      <p className="ml-waitlist__disabled" role="status">
        {fullReleaseWaitlist.disabledHint}
      </p>
    );
  }

  if (status === 'success') {
    return (
      <div className="ml-waitlist__successState">
        <p className="ml-waitlist__success" role="status">
          {message}
        </p>
        {referralUrl ? (
          <div className="ml-waitlist__referral">
            <p className="ml-waitlist__referralHeadline">{fullReleaseWaitlist.referralHeadline}</p>
            <p className="ml-waitlist__referralBody ml-text-body-secondary">
              {fullReleaseWaitlist.referralBody}
            </p>
            <button
              type="button"
              className="ml-btn ml-btn--outline ml-waitlist__referralCta"
              onClick={() => void onCopyReferralLink()}>
              {referralCopied
                ? fullReleaseWaitlist.referralCopied
                : fullReleaseWaitlist.referralCta}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <form className="ml-waitlistForm" onSubmit={onSubmit} noValidate>
      <div className="ml-waitlistForm__field">
        <label className="ml-waitlistForm__label" htmlFor="waitlist-email">
          {fullReleaseWaitlist.emailLabel}
        </label>
        <input
          id="waitlist-email"
          className="ml-waitlistForm__input"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
        />
      </div>
      <div className="ml-waitlistForm__field">
        <label className="ml-waitlistForm__label" htmlFor="waitlist-first">
          {fullReleaseWaitlist.firstNameLabel}
        </label>
        <input
          id="waitlist-first"
          className="ml-waitlistForm__input"
          type="text"
          name="firstName"
          autoComplete="given-name"
          value={firstName}
          onChange={(ev) => setFirstName(ev.target.value)}
        />
      </div>
      <div className="ml-waitlistForm__field">
        <label className="ml-waitlistForm__label" htmlFor="waitlist-city">
          {fullReleaseWaitlist.cityLabel}
        </label>
        {/* A select, not a text box. Free text made "austin", "atx" and "austin, tx" three separate
            metros, which a city-by-city rollout cannot group — and grouping is the whole point of
            asking. Required, because a signup with no metro cannot be invited by the only mechanism
            that will ever invite anyone. */}
        <select
          id="waitlist-city"
          className="ml-waitlistForm__input"
          name="city"
          required
          value={citySlug}
          onChange={(ev) => setCitySlug(ev.target.value)}>
          <option value="" disabled>
            {fullReleaseWaitlist.cityPlaceholder}
          </option>
          {WAITLIST_METROS.map((m) => (
            <option key={m.slug} value={m.slug}>
              {m.label}
            </option>
          ))}
        </select>
        {citySlug === WAITLIST_METRO_OTHER ? (
          <input
            className="ml-waitlistForm__input ml-waitlistForm__input--sub"
            type="text"
            name="cityOther"
            autoComplete="address-level2"
            placeholder={fullReleaseWaitlist.cityOtherPlaceholder}
            value={cityOther}
            onChange={(ev) => setCityOther(ev.target.value)}
          />
        ) : null}
      </div>
      {status === 'error' ? (
        <p className="ml-waitlistForm__err" role="alert">
          {message || fullReleaseWaitlist.errorGeneric}
        </p>
      ) : null}
      <button
        type="submit"
        className="ml-btn ml-btn--primary ml-ctaNarrow ml-waitlistForm__submit"
        disabled={status === 'loading'}
        aria-busy={status === 'loading'}>
        {status === 'loading' ? (
          <span className="ml-waitlistForm__spinner" role="status" aria-label="Joining waitlist" />
        ) : (
          fullReleaseWaitlist.submitLabel
        )}
      </button>
      <p className="ml-waitlistForm__proof ml-text-body-secondary">
        {fullReleaseWaitlist.socialProofNearCta}
      </p>
    </form>
  );
}
